"use client";

import { useMemo } from 'react'
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { NetWorthData } from '@/lib/finance/types'
import { dateToMs, fmtMsDate } from '@/lib/finance/format'
import { Pill } from '@/components/finance/ui'
import { ChartTooltip, crosshair } from '@/components/finance/ChartTooltip'
import { ChartCursorLayer, pinnedIndex, type ChartCursor } from '@/components/finance/chartCursor'
import { timeXAxisProps, timeTickFormatter, yAxisProps, gridProps, CHART_MARGIN, ACTIVE_DOT, downsample } from '@/components/finance/chartDefaults'

/**
 * The net-worth hero chart shared by Overview and Net Worth: a filled
 * net-worth line with optional Assets / Debts / FIRE overlays toggled by
 * pills. The two pages previously carried line-for-line copies of the
 * overlay config, row shaping, pill row, and chart body.
 */

export const NW_OVERLAYS = [
  { key: 'netWorth', label: 'Net worth', color: 'var(--series-1)' },
  { key: 'assets', label: 'Assets', color: 'var(--series-2)' },
  { key: 'debts', label: 'Debts', color: 'var(--series-6)' },
  { key: 'fire', label: 'FIRE', color: 'var(--series-5)' },
] as const

export interface NwRow {
  month: string
  t: number
  netWorth: number | null
  assets: number | null
  debts: number | null
  fire: number | null
}

/** Shape the range-filtered months of a net-worth series into chart rows,
 * downsampled to chart density (the daily model produces thousands of dates).
 * Debts keep their stored negative sign, plotting below the zero line. */
export function useNetWorthRows(
  networth: NetWorthData,
  startMonth: string,
  endMonth: string,
): NwRow[] {
  const { months, series: s } = networth
  return useMemo(
    () =>
      downsample(months.filter((m) => m >= startMonth && m <= endMonth), (m) => s.netWorth?.[m]).map((m) => ({
        month: m,
        t: dateToMs(m),
        netWorth: s.netWorth?.[m] ?? null,
        assets: s.assets?.[m] ?? null,
        debts: s.debts?.[m] ?? null,
        fire: s.fireNetWorth?.[m] ?? null,
      })),
    [months, s, startMonth, endMonth],
  )
}

/** The overlay pill row — every series (net worth included) is toggleable. */
export function NetWorthOverlayPills({
  overlays,
  onToggle,
}: {
  overlays: Set<string>
  onToggle: (key: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {NW_OVERLAYS.map((o) => (
        <Pill
          key={o.key}
          label={o.label}
          color={o.color}
          shape="line"
          active={overlays.has(o.key)}
          onClick={() => onToggle(o.key)}
        />
      ))}
    </div>
  )
}

export function NetWorthChart({
  rows,
  overlays,
  startMonth,
  endMonth,
  minTickGap = 40,
  cursor,
}: {
  rows: NwRow[]
  overlays: Set<string>
  /** The SELECTED range bounds (from usePageRange) — tick granularity follows
   * the selection, not the data that happens to fill it. */
  startMonth: string
  endMonth: string
  minTickGap?: number
  /** Page-wide hover sync + pinned date (the Net Worth page); omitted on the
   * Overview, where this is the only time chart. */
  cursor?: ChartCursor
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart key={cursor?.resetKey ?? 0} data={rows} margin={CHART_MARGIN} {...cursor?.chartProps}>
        <CartesianGrid {...gridProps} />
        <XAxis {...timeXAxisProps} tickFormatter={timeTickFormatter(startMonth, endMonth)} minTickGap={minTickGap} />
        <YAxis {...yAxisProps} domain={['auto', 'auto']} />
        <Tooltip cursor={crosshair} defaultIndex={pinnedIndex(rows, cursor?.pinnedT ?? null)} content={<ChartTooltip labelFormatter={fmtMsDate} />} />
        {overlays.has('netWorth') && (
          <Area type="linear"
            dataKey="netWorth"
            name="Net worth"
            stroke="var(--series-1)"
            strokeWidth={2}
            fill="var(--series-1)"
            fillOpacity={0.08}
            dot={false}
            activeDot={ACTIVE_DOT}
          />
        )}
        {overlays.has('fire') && (
          <Area type="linear"
            dataKey="fire"
            name="FIRE net worth"
            stroke="var(--series-5)"
            strokeWidth={2}
            fill="var(--series-5)"
            fillOpacity={0.08}
            dot={false}
            activeDot={ACTIVE_DOT}
          />
        )}
        {overlays.has('assets') && (
          <Line type="linear" dataKey="assets" name="Assets" stroke="var(--series-2)" strokeWidth={2} dot={false} />
        )}
        {overlays.has('debts') && (
          <Line type="linear" dataKey="debts" name="Debts" stroke="var(--series-6)" strokeWidth={2} dot={false} />
        )}
        {cursor && <ChartCursorLayer cursor={cursor} />}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
